import React, { useState, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { ImageUpload } from './ImageUpload';
import { Image as ImageIcon, Palette, Type, Save, Eye, Code, X, Bold, Italic, Underline, List, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Heading1, Heading2, Heading3, File, Upload } from 'lucide-react';
import { useToast } from './Toast';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { FileButton } from './FileButtonExtension';

// Website-style email template matching FreeSpiritFabiha landing page
// Full-width design with decorative elements, hero section, and website-like layout
const EMAIL_TEMPLATE = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    /* Email-safe heading styles matching website branding */
    h1, h2, h3, h4, h5, h6 {
      font-family: 'Georgia', serif;
      color: #1f2937;
      font-weight: 600;
      margin: 1em 0 0.5em 0;
      line-height: 1.2;
    }
    h1 {
      font-size: 2.25em;
      line-height: 1.1;
    }
    h2 {
      font-size: 1.875em;
      line-height: 1.2;
    }
    h3 {
      font-size: 1.5em;
      line-height: 1.3;
    }
    h4 {
      font-size: 1.25em;
      line-height: 1.4;
    }
    p {
      margin: 0.75em 0;
      line-height: 1.7;
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #fdfbf7; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #fdfbf7; padding: 0; margin: 0;">
    <tr>
      <td align="center" style="padding: 0;">
        <!-- Navigation Header - Matching landing page nav -->
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #fdfbf7; border-bottom: 1px solid #eaddcf;">
          <tr>
            <td align="center" style="padding: 16px 20px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 1200px;">
                <tr>
                  <td style="vertical-align: middle;">
                    <h1 style="margin: 0; font-size: 20px; font-family: 'Georgia', serif; color: #c5a059; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;">{{brandName}}</h1>
                  </td>
                  {{profilePicture}}
                </tr>
              </table>
            </td>
          </tr>
        </table>

        <!-- Hero Section with Decorative Background - Matching landing page -->
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #fdfbf7; position: relative; overflow: hidden;">
          <!-- Decorative Background Blobs (simulated with gradient) -->
          <tr>
            <td style="background: linear-gradient(135deg, #fae1e5 0%, #fdfbf7 30%, #fdfbf7 70%, #f3e6d5 100%); padding: 0;">
              <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 1200px; margin: 0 auto;">
                <tr>
                  <td align="center" style="padding: 60px 40px 80px 40px;">
                    <!-- Hero Content Area -->
                    <table width="100%" cellpadding="0" cellspacing="0" style="background-color: transparent;">
                      <tr>
                        <td style="padding: 0;">
                          <!-- Main Content - Website-style layout -->
                          <div style="max-width: 800px; margin: 0 auto; background-color: transparent; line-height: 1.7; color: #374151; font-size: 16px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;">
                            {{content}}
                          </div>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        <!-- Footer Section - Matching landing page footer -->
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #fdfbf7; border-top: 1px solid #eaddcf;">
          <tr>
            <td align="center" style="padding: 40px 20px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 1200px;">
                <tr>
                  <td style="text-align: center;">
                    <p style="margin: 0 0 12px 0; font-family: 'Georgia', serif; color: #4b5563; font-size: 14px; line-height: 1.6;">
                      With love and light,<br>
                      <strong style="color: #c5a059; font-weight: 600; font-family: 'Georgia', serif;">{{brandName}}</strong>
                    </p>
                    <p style="margin: 0 0 20px 0; font-family: 'Georgia', serif; color: #6b7280; font-size: 12px;">
                      © {{year}} {{brandName}}. All rights reserved.
                    </p>
                    <p style="margin: 0; font-family: 'Georgia', serif; color: #9ca3af; font-size: 11px; line-height: 1.5;">
                      You're receiving this email because you subscribed or interacted with {{brandName}}.<br>
                      <a href="{{unsubscribeUrl}}" style="color: #c5a059; text-decoration: underline;">Unsubscribe</a> | <a href="mailto:Fabiha@freespiritfabiha.com" style="color: #c5a059; text-decoration: underline;">Update Preferences</a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`;

// Layout Wrapper Component - Website Style Only (Full-width like landing page)
function EmailLayoutWrapper({ brandName, profilePicture, children }) {
  const year = new Date().getFullYear();
  
  // Build header content with profile picture if available - Matching landing page nav styling
  let headerContent = `<h1 style="margin: 0; font-size: 20px; font-family: 'Georgia', serif; color: #c5a059; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; display: inline-block;">${brandName}</h1>`;
  
  if (profilePicture) {
    headerContent += `<div style="float: right; margin-left: 16px;">
      <div style="width: 60px; height: 60px; border-radius: 50%; overflow: hidden; border: 3px solid #c5a059; box-shadow: 0 2px 8px rgba(197, 160, 89, 0.2);">
        <img src="${profilePicture}" alt="${brandName}" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
      </div>
    </div>`;
  }

  return (
    <div 
      style={{
        width: '100%',
        backgroundColor: '#fdfbf7',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
      }}
      className="email-layout-outer"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Navigation Header - Full width like landing page */}
      <div 
        style={{
          backgroundColor: '#fdfbf7',
          borderBottom: '1px solid #eaddcf',
          padding: '16px 20px',
          pointerEvents: 'none',
          overflow: 'hidden',
          maxWidth: '1200px',
          margin: '0 auto'
        }}
        className="email-layout-header"
        dangerouslySetInnerHTML={{ __html: headerContent }}
      />
      
      {/* Hero Section with Decorative Background - Matching landing page */}
      <div 
        style={{
          background: 'linear-gradient(135deg, #fae1e5 0%, #fdfbf7 30%, #fdfbf7 70%, #f3e6d5 100%)',
          padding: '60px 40px 80px 40px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Content Area - Centered with max-width like landing page */}
        <div 
          style={{
            maxWidth: '800px',
            margin: '0 auto',
            backgroundColor: 'transparent',
            position: 'relative',
            zIndex: 1
          }}
          className="email-layout-inner"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function EmailEditor({ value, onChange, brandName = 'FreeSpiritFabiha', profilePicture = null, showPreview = true }) {
  const [previewMode, setPreviewMode] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [rawHtml, setRawHtml] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [innerContent, setInnerContent] = useState('');
  const { showToast } = useToast();

  // Extract inner content from website template
  const extractContent = (html) => {
    if (!html) return '';
    
    // Extract content from the main content div (max-width: 800px)
    // This is the div that contains {{content}} in the template
    const contentMatch = html.match(/<div[^>]*style="[^"]*max-width:\s*800px[^"]*"[^>]*>([\s\S]*?)<\/div>/i);
    
    if (contentMatch && contentMatch[1]) {
      const extracted = contentMatch[1].trim();
      // Make sure it's not just header/footer content
      if (extracted && 
          !extracted.includes('FreeSpiritFabiha') && 
          !extracted.match(/<h1[^>]*>.*FreeSpiritFabiha.*<\/h1>/i) &&
          !extracted.includes('With love and light') &&
          !extracted.includes('Unsubscribe') &&
          !extracted.includes('{{content}}')) {
        return extracted;
      }
    }
    
    // Fallback: try to find content div with line-height 1.7 (main content area)
    const contentMatch2 = html.match(/<div[^>]*style="[^"]*line-height:\s*1\.7[^"]*"[^>]*>([\s\S]*?)<\/div>/i);
    if (contentMatch2 && contentMatch2[1]) {
      const extracted = contentMatch2[1].trim();
      if (extracted && 
          !extracted.includes('FreeSpiritFabiha') && 
          !extracted.includes('With love and light') &&
          !extracted.includes('Unsubscribe')) {
        return extracted;
      }
    }
    
    // Fallback: try to find any div with substantial content that's not header/footer
    const allDivs = html.match(/<div[^>]*>([\s\S]*?)<\/div>/gi);
    if (allDivs) {
      for (const div of allDivs) {
        const innerMatch = div.match(/<div[^>]*>([\s\S]*?)<\/div>/i);
        if (innerMatch && innerMatch[1]) {
          const content = innerMatch[1].trim();
          // Skip if it's header, footer, or template structure
          if (content && 
              content.length > 20 &&
              !content.includes('FreeSpiritFabiha') &&
              !content.includes('With love and light') &&
              !content.includes('Unsubscribe') &&
              !content.includes('<!DOCTYPE') &&
              !content.includes('<html') &&
              !content.includes('<body') &&
              !content.includes('<table')) {
            return content;
          }
        }
      }
    }
    
    // Last resort: return as-is if no template detected
    return html;
  };

  // Initialize inner content from value - only run once on mount
  useEffect(() => {
    if (value) {
      const extracted = extractContent(value);
      // Only set if extraction was successful and doesn't contain header
      if (extracted && 
          !extracted.includes('FreeSpiritFabiha') && 
          !extracted.match(/<h1[^>]*>.*FreeSpiritFabiha.*<\/h1>/i) &&
          extracted.length > 10) {
        setInnerContent(extracted);
      } else {
        // Start with empty if extraction failed or contains header
        setInnerContent('');
      }
    } else {
      setInnerContent('');
    }
  }, []); // Only run on mount

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto; border-radius: 4px;',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: 'color: #c5a059; text-decoration: underline;',
        },
      }),
      FileButton, // Custom file button node extension
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'fileButton'],
      }),
      Highlight.configure({
        multicolor: true,
      }),
    ],
    content: innerContent || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Prevent saving if content contains header (shouldn't happen, but safety check)
      if (html && !html.includes('FreeSpiritFabiha') && !html.match(/<h1[^>]*>.*FreeSpiritFabiha.*<\/h1>/i)) {
        setInnerContent(html);
        // Wrap content in website template before saving
        const wrapped = wrapContentInLayout(html);
        onChange(wrapped);
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-xl mx-auto focus:outline-none min-h-[400px] p-6',
        style: 'min-height: 400px;',
        'data-placeholder': 'Start writing your email... Use variables like {{name}}, {{orderId}}, etc.',
      },
    },
  });

  // Add inline styles to headings for email client compatibility
  const addHeadingStyles = (html) => {
    if (!html) return html;
    
    // Add inline styles to H1, H2, H3, H4 elements
    // H1 styles
    html = html.replace(/<h1([^>]*)>/gi, (match, attrs) => {
      // Check if style already exists
      if (attrs && attrs.includes('style=')) {
        // Append to existing style
        return match.replace(/style="([^"]*)"/i, (styleMatch, existingStyle) => {
          return `style="${existingStyle}; font-family: 'Georgia', serif; font-size: 2.25em; font-weight: 600; color: #1f2937; margin: 1em 0 0.5em 0; line-height: 1.1;"`;
        });
      } else {
        // Add new style attribute
        return `<h1${attrs} style="font-family: 'Georgia', serif; font-size: 2.25em; font-weight: 600; color: #1f2937; margin: 1em 0 0.5em 0; line-height: 1.1;">`;
      }
    });
    
    // H2 styles
    html = html.replace(/<h2([^>]*)>/gi, (match, attrs) => {
      if (attrs && attrs.includes('style=')) {
        return match.replace(/style="([^"]*)"/i, (styleMatch, existingStyle) => {
          return `style="${existingStyle}; font-family: 'Georgia', serif; font-size: 1.875em; font-weight: 600; color: #1f2937; margin: 1em 0 0.5em 0; line-height: 1.2;"`;
        });
      } else {
        return `<h2${attrs} style="font-family: 'Georgia', serif; font-size: 1.875em; font-weight: 600; color: #1f2937; margin: 1em 0 0.5em 0; line-height: 1.2;">`;
      }
    });
    
    // H3 styles
    html = html.replace(/<h3([^>]*)>/gi, (match, attrs) => {
      if (attrs && attrs.includes('style=')) {
        return match.replace(/style="([^"]*)"/i, (styleMatch, existingStyle) => {
          return `style="${existingStyle}; font-family: 'Georgia', serif; font-size: 1.5em; font-weight: 600; color: #1f2937; margin: 1em 0 0.5em 0; line-height: 1.3;"`;
        });
      } else {
        return `<h3${attrs} style="font-family: 'Georgia', serif; font-size: 1.5em; font-weight: 600; color: #1f2937; margin: 1em 0 0.5em 0; line-height: 1.3;">`;
      }
    });
    
    // H4 styles
    html = html.replace(/<h4([^>]*)>/gi, (match, attrs) => {
      if (attrs && attrs.includes('style=')) {
        return match.replace(/style="([^"]*)"/i, (styleMatch, existingStyle) => {
          return `style="${existingStyle}; font-family: 'Georgia', serif; font-size: 1.25em; font-weight: 600; color: #1f2937; margin: 1em 0 0.5em 0; line-height: 1.4;"`;
        });
      } else {
        return `<h4${attrs} style="font-family: 'Georgia', serif; font-size: 1.25em; font-weight: 600; color: #1f2937; margin: 1em 0 0.5em 0; line-height: 1.4;">`;
      }
    });
    
    return html;
  };

  // Convert file buttons to email-compatible table-based buttons
  // Handles both FileButton nodes (from extension) and legacy HTML buttons
  const convertFileButtonsToEmailFormat = (html) => {
    if (!html) return html;
    
    // Pattern 1: FileButton node output - <p><a data-file-button="true">...</a></p>
    const buttonPattern1 = /<p[^>]*style="[^"]*text-align:\s*center[^"]*"[^>]*>\s*<a[^>]*data-file-button="true"[^>]*href="([^"]+)"[^>]*>([^<]+)<\/a>\s*<\/p>/gi;
    // Pattern 2: Links with pink background in centered paragraphs (legacy)
    const buttonPattern2 = /<p[^>]*style="[^"]*text-align:\s*center[^"]*"[^>]*>\s*<a[^>]*style="[^"]*background-color:\s*#d48c95[^"]*"[^>]*href="([^"]+)"[^>]*>([^<]+)<\/a>\s*<\/p>/gi;
    
    const convertButton = (url, linkText) => {
      const textContent = linkText.trim();
      // Convert to email-compatible table-based button
      return `
        <div style="margin: 20px 0; text-align: center;">
          <table cellpadding="0" cellspacing="0" border="0" style="margin: 0 auto; border-radius: 8px; overflow: hidden;">
            <tr>
              <td align="center" style="background-color: #d48c95; padding: 14px 28px; border-radius: 8px;">
                <a href="${url}" target="_blank" style="display: inline-block; text-decoration: none; color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif; font-size: 16px; font-weight: 600; line-height: 1.5; white-space: nowrap;">
                  ${textContent}
                </a>
              </td>
            </tr>
          </table>
        </div>
      `;
    };
    
    let result = html;
    
    // Replace FileButton nodes (with data-file-button attribute)
    result = result.replace(buttonPattern1, (match, url, linkText) => convertButton(url, linkText));
    
    // Replace legacy buttons (pink background)
    result = result.replace(buttonPattern2, (match, url, linkText) => convertButton(url, linkText));
    
    return result;
  };

  // Wrap content in website template
  const wrapContentInLayout = (content) => {
    const year = new Date().getFullYear();
    let template = EMAIL_TEMPLATE
      .replace(/{{brandName}}/g, brandName)
      .replace(/{{year}}/g, year);
    
    // Add profile picture to header if available - Matching landing page profile picture styling
    let profilePictureHtml = '';
    if (profilePicture) {
      profilePictureHtml = `<td align="right" style="vertical-align: middle; width: 80px;">
        <div style="display: inline-block; width: 60px; height: 60px; border-radius: 50%; overflow: hidden; border: 3px solid #c5a059; box-shadow: 0 2px 8px rgba(197, 160, 89, 0.2);">
          <img src="${profilePicture}" alt="${brandName}" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
        </div>
      </td>`;
    }
    template = template.replace('{{profilePicture}}', profilePictureHtml);

    // Add inline styles to headings for email client compatibility
    const styledContent = addHeadingStyles(content || '<p>Your email content here...</p>');
    
    // Convert file buttons to email-compatible format before inserting
    const processedContent = convertFileButtonsToEmailFormat(styledContent);

    return template.replace('{{content}}', processedContent);
  };

  // Sync editor with innerContent - but prevent header content
  useEffect(() => {
    if (editor) {
      const currentContent = editor.getHTML();
      // Only update if innerContent is different and doesn't contain header
      if (innerContent !== undefined && 
          innerContent !== currentContent && 
          !innerContent.includes('FreeSpiritFabiha') &&
          !innerContent.match(/<h1[^>]*>.*FreeSpiritFabiha.*<\/h1>/i)) {
        editor.commands.setContent(innerContent || '');
      } else if (currentContent && (currentContent.includes('FreeSpiritFabiha') || currentContent.match(/<h1[^>]*>.*FreeSpiritFabiha.*<\/h1>/i))) {
        // If editor somehow has header content, clear it
        editor.commands.clearContent();
        setInnerContent('');
      }
    }
  }, [innerContent, editor]);

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Validate size
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size must be less than 5MB', 'error');
        return;
      }

      try {
        // Upload to Firebase Storage
        const { storage } = await import('../firebase/config');
        const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
        
        const filename = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
        const storageRef = ref(storage, `email-images/${filename}`);
        
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        // Insert image into editor
        if (editor) {
          editor.chain().focus().setImage({ src: url }).run();
        }

        showToast('Image uploaded successfully', 'success');
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Upload error:', error);
        }
        showToast('Failed to upload image', 'error');
      }
    };
  };

  const handleFileUpload = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Validate size (50MB max for SFTP)
      if (file.size > 50 * 1024 * 1024) {
        showToast('File size must be less than 50MB', 'error');
        return;
      }

      try {
        showToast('Uploading file to server...', 'info');
        
        // Read file as base64
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const base64Data = event.target.result.split(',')[1]; // Remove data:type;base64, prefix
            
            // Call Cloud Function to upload to SFTP
            const uploadFileToSFTP = httpsCallable(functions, 'uploadFileToSFTP');
            const result = await uploadFileToSFTP({
              fileData: base64Data,
              fileName: file.name,
              folder: 'email-files',
              mimeType: file.type
            });

            if (result.data.success) {
              // Insert file button using the custom FileButton node extension
              if (editor) {
                const fileUrl = result.data.publicUrl;
                const fileName = result.data.fileName;
                
                // Insert using the FileButton node extension (adheres to Tiptap schema)
                editor.chain().focus().insertContent({
                  type: 'fileButton',
                  attrs: {
                    url: fileUrl,
                    fileName: fileName,
                  },
                }).run();
              }

              showToast('File uploaded and inserted successfully', 'success');
            } else {
              throw new Error(result.data.error || 'Upload failed');
            }
          } catch (error) {
            console.error('Upload error:', error);
            let errorMessage = 'Failed to upload file';
            
            if (error.message?.includes('permission-denied')) {
              errorMessage = 'You are not authorized to upload files.';
            } else if (error.message?.includes('unauthenticated')) {
              errorMessage = 'Please log in to upload files.';
            } else {
              errorMessage = `Failed to upload file: ${error.message || 'Unknown error'}`;
            }
            
            showToast(errorMessage, 'error');
          }
        };
        
        reader.onerror = () => {
          showToast('Failed to read file', 'error');
        };
        
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('File read error:', error);
        showToast('Failed to read file', 'error');
      }
    };
  };

  const handleAddLink = () => {
    if (!linkUrl.trim()) return;
    if (editor) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkDialog(false);
    }
  };

  const getPreviewHtml = () => {
    if (htmlMode) {
      return rawHtml;
    }
    const content = editor?.getHTML() || innerContent || '';
    return wrapContentInLayout(content);
  };

  if (!editor) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin text-[#c5a059]">Loading editor...</div></div>;
  }

  return (
    <div className="space-y-4">
      {/* Main Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200">
        <button
          onClick={() => {
            setHtmlMode(!htmlMode);
            if (!htmlMode) {
              setRawHtml(editor.getHTML());
            } else {
              editor.commands.setContent(rawHtml);
            }
          }}
          className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 border rounded-lg transition text-xs sm:text-sm font-medium min-h-[44px] min-w-[44px] ${
            htmlMode 
              ? 'bg-[#c5a059] text-white border-[#c5a059]' 
              : 'bg-white border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Code size={16} className="sm:mr-0" />
          <span className="hidden sm:inline">HTML</span>
        </button>

        {showPreview && (
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 border rounded-lg transition text-xs sm:text-sm font-medium min-h-[44px] min-w-[44px] ${
              previewMode 
                ? 'bg-[#c5a059] text-white border-[#c5a059]' 
                : 'bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Eye size={16} className="sm:mr-0" />
            <span className="hidden sm:inline">Preview</span>
          </button>
        )}
      </div>

      {/* Formatting Toolbar */}
      {!htmlMode && (
        <div className="flex flex-wrap items-center gap-1 p-2 bg-white border border-gray-200 rounded-lg overflow-x-auto">
          {/* Text Formatting */}
          <div className="flex items-center gap-1 border-r border-gray-200 pr-2 mr-2 flex-shrink-0">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive('bold') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Bold"
            >
              <Bold size={18} />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive('italic') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Italic"
            >
              <Italic size={18} />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive('underline') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Underline"
            >
              <Underline size={18} />
            </button>
          </div>

          {/* Headings */}
          <div className="flex items-center gap-1 border-r border-gray-200 pr-2 mr-2 flex-shrink-0">
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive('heading', { level: 1 }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Heading 1"
            >
              <Heading1 size={18} />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive('heading', { level: 2 }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Heading 2"
            >
              <Heading2 size={18} />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive('heading', { level: 3 }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Heading 3"
            >
              <Heading3 size={18} />
            </button>
          </div>

          {/* Lists */}
          <div className="flex items-center gap-1 border-r border-gray-200 pr-2 mr-2 flex-shrink-0">
            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive('bulletList') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Bullet List"
            >
              <List size={18} />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive('orderedList') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Numbered List"
            >
              <List size={18} className="rotate-90" />
            </button>
          </div>

          {/* Alignment */}
          <div className="flex items-center gap-1 border-r border-gray-200 pr-2 mr-2 flex-shrink-0">
            <button
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive({ textAlign: 'left' }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Align Left"
            >
              <AlignLeft size={18} />
            </button>
            <button
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive({ textAlign: 'center' }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Align Center"
            >
              <AlignCenter size={18} />
            </button>
            <button
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive({ textAlign: 'right' }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Align Right"
            >
              <AlignRight size={18} />
            </button>
          </div>

          {/* Media */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={handleImageUpload}
              className="p-2 rounded hover:bg-gray-100 transition text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Insert Image"
            >
              <ImageIcon size={18} />
            </button>
            <button
              onClick={handleFileUpload}
              className="p-2 rounded hover:bg-gray-100 transition text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Upload File (to SFTP)"
            >
              <File size={18} />
            </button>
            <button
              onClick={() => {
                const url = editor.getAttributes('link').href || '';
                setLinkUrl(url);
                setShowLinkDialog(true);
              }}
              className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
                editor.isActive('link') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
              }`}
              title="Insert Link"
            >
              <LinkIcon size={18} />
            </button>
          </div>

          {/* Colors */}
          <div className="flex items-center gap-1 ml-auto sm:ml-auto border-l border-gray-200 pl-2 flex-shrink-0">
            <input
              type="color"
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
              className="w-10 h-10 sm:w-8 sm:h-8 rounded border border-gray-300 cursor-pointer min-h-[44px] min-w-[44px]"
              title="Text Color"
            />
            <input
              type="color"
              onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
              className="w-10 h-10 sm:w-8 sm:h-8 rounded border border-gray-300 cursor-pointer min-h-[44px] min-w-[44px]"
              title="Highlight Color"
            />
          </div>
        </div>
      )}

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-lg">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon size={18} />
            <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Add Link</h3>
            <button
              onClick={() => {
                setShowLinkDialog(false);
                setLinkUrl('');
              }}
              className="ml-auto p-2 text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] text-base min-h-[44px]"
              onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
            />
            <button
              onClick={handleAddLink}
              className="px-4 py-2.5 sm:py-2 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition min-h-[44px] font-medium"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Editor */}
      {htmlMode ? (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">HTML Content</label>
          <textarea
            value={rawHtml}
            onChange={(e) => setRawHtml(e.target.value)}
            className="w-full h-96 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] font-mono text-sm"
            placeholder="Enter HTML content..."
          />
        </div>
      ) : (
        <EmailLayoutWrapper brandName={brandName} profilePicture={profilePicture}>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <EditorContent editor={editor} />
          </div>
        </EmailLayoutWrapper>
      )}

      {/* Preview */}
      {previewMode && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Email Preview</h3>
            <button
              onClick={() => setPreviewMode(false)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          </div>
          <div 
            className="border border-[#eaddcf] rounded-lg p-4 sm:p-8 bg-[#fdfbf7]"
            style={{ maxWidth: '100%', margin: '0 auto' }}
          >
            <div 
              className="mx-auto"
              style={{ maxWidth: '600px' }}
              dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
            />
          </div>
        </div>
      )}

      {/* Custom Styles for Tiptap */}
      <style>{`
        .email-layout-outer {
          pointer-events: none;
        }
        .email-layout-outer > * {
          pointer-events: auto;
        }
        .email-layout-header {
          pointer-events: none !important;
        }
        .email-layout-inner {
          pointer-events: auto !important;
        }
        .email-layout-inner > * {
          pointer-events: auto !important;
        }
        .ProseMirror {
          outline: none;
          min-height: 400px;
          padding: 24px;
          font-size: 16px;
          line-height: 1.8;
          color: #1f2937;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          background: transparent;
          position: relative;
          z-index: 10;
          pointer-events: auto !important;
        }
        .ProseMirror p {
          margin: 0.75em 0;
          color: #374151;
          line-height: 1.7;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
          font-style: italic;
        }
        .ProseMirror h1 {
          font-size: 2.25em;
          font-weight: 600;
          margin: 0.75em 0 0.5em 0;
          color: #1f2937;
          line-height: 1.2;
          font-family: 'Georgia', serif;
        }
        .ProseMirror h2 {
          font-size: 1.875em;
          font-weight: 600;
          margin: 0.75em 0 0.5em 0;
          color: #1f2937;
          line-height: 1.3;
          font-family: 'Georgia', serif;
        }
        .ProseMirror h3 {
          font-size: 1.5em;
          font-weight: 600;
          margin: 0.75em 0 0.5em 0;
          color: #1f2937;
          line-height: 1.4;
          font-family: 'Georgia', serif;
        }
        .ProseMirror h4 {
          font-size: 1.25em;
          font-weight: 600;
          margin: 0.75em 0 0.5em 0;
          color: #1f2937;
          font-family: 'Georgia', serif;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.75em;
          margin: 0.75em 0;
        }
        .ProseMirror li {
          margin: 0.5em 0;
          line-height: 1.6;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1.5em 0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .ProseMirror a {
          color: #c5a059;
          text-decoration: underline;
          transition: color 0.2s;
          font-weight: 500;
        }
        .ProseMirror a:hover {
          color: #d48c95;
        }
        /* File button styling - for FileButton node extension */
        .ProseMirror a[data-file-button="true"],
        .ProseMirror p[style*="text-align: center"] a[data-file-button="true"],
        .ProseMirror p[style*="text-align: center"] a[style*="background-color: #d48c95"],
        .ProseMirror a[style*="background-color: #d48c95"] {
          display: inline-block !important;
          background-color: #d48c95 !important;
          color: #ffffff !important;
          padding: 14px 28px !important;
          border-radius: 8px !important;
          text-decoration: none !important;
          font-weight: 600 !important;
          margin: 20px 0 !important;
          border: none !important;
          cursor: pointer !important;
          font-size: 16px !important;
          line-height: 1.5 !important;
          white-space: nowrap !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif !important;
        }
        .ProseMirror p[style*="text-align: center"] {
          text-align: center !important;
        }
        .ProseMirror table {
          margin: 1.5em 0;
          border-collapse: collapse;
        }
        .ProseMirror table td {
          padding: 0;
        }
        .ProseMirror table a {
          text-decoration: none !important;
          color: #ffffff !important;
          display: inline-block;
        }
        .ProseMirror table td[style*="background-color: #d48c95"] {
          background-color: #d48c95 !important;
          border-radius: 8px;
        }
        .ProseMirror table td[style*="background-color: #d48c95"]:hover {
          background-color: #c07b83 !important;
          transition: background-color 0.2s;
        }
        .ProseMirror a[style*="background-color: #d48c95"] {
          display: inline-block !important;
          background-color: #d48c95 !important;
          color: #ffffff !important;
          padding: 14px 28px !important;
          border-radius: 8px !important;
          text-decoration: none !important;
          font-weight: 600 !important;
          margin: 20px 0 !important;
        }
        .ProseMirror p[style*="text-align: center"] a[style*="background-color: #d48c95"] {
          margin: 20px auto !important;
        }
        .ProseMirror blockquote {
          border-left: 5px solid #c5a059;
          padding-left: 1.5em;
          margin: 1.5em 0;
          font-style: italic;
          color: #4b5563;
          background: linear-gradient(135deg, #fae1e5, #f3e6d5);
          padding: 1.5em 2em;
          border-radius: 8px;
          font-family: 'Georgia', serif;
        }
        .ProseMirror code {
          background: #f3f4f6;
          padding: 0.2em 0.5em;
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9em;
          color: #dc2626;
        }
        .ProseMirror pre {
          background: #1f2937;
          color: #f9fafb;
          padding: 1.25em;
          border-radius: 6px;
          overflow-x: auto;
          margin: 1.5em 0;
        }
        .ProseMirror pre code {
          background: none;
          padding: 0;
          color: inherit;
        }
        .ProseMirror strong {
          font-weight: 600;
          color: #1f2937;
        }
        .ProseMirror em {
          font-style: italic;
          color: #4b5563;
        }
        .ProseMirror em {
          font-style: italic;
        }
        .ProseMirror u {
          text-decoration: underline;
        }
        .ProseMirror mark {
          background-color: #fef08a;
          padding: 0.1em 0.2em;
          border-radius: 2px;
        }
        .ProseMirror hr {
          border: none;
          border-top: 2px solid #e5e7eb;
          margin: 2em 0;
        }
      `}</style>
    </div>
  );
}

